import { usePortfolioOverview } from "../../helpers/hooks/use-portfolio-overview";
import type { PortfolioStats } from "../../helpers/api/crm";

/**
 * Six-card CS-leader overview that sits above the customer table. Mirrors
 * the /billing page's card strip (bg-surface-primary rounded-lg shadow-sm
 * border). Values come from the nightly portfolio_snapshot row so the
 * render is O(1).
 *
 * `arrOverride` lets the parent (CustomerList) inject a recomputed ARR total
 * when the user toggles the per-row ACV/ARR mode (e.g. "avg of last 3 months
 * × 12") so the headline card and the table column stay in lock-step.
 */

interface StatCardProps {
  label: string;
  value: string;
  /** brand = teal (positive), warn = pink (negative). Default = neutral text. */
  accent?: "default" | "brand" | "warn";
  hint?: string;
}

function StatCard({ label, value, accent = "default", hint }: StatCardProps) {
  const cls =
    accent === "brand"
      ? "text-brand-primary"
      : accent === "warn"
        ? "text-brand-secondary"
        : "text-content-primary";
  return (
    <div className="bg-surface-primary rounded-lg p-4 shadow-sm border border-[#E9E9EB]">
      <div className="text-xs text-content-tertiary mb-1">{label}</div>
      <div className={`text-2xl font-semibold ${cls}`}>{value}</div>
      {hint && (
        <div className="text-[11px] text-content-tertiary mt-1">{hint}</div>
      )}
    </div>
  );
}

function formatUsd(n: number | null | undefined): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function formatInt(n: number | null | undefined): string {
  if (n == null) return "—";
  return n.toLocaleString();
}

function bandLabel(dist: Record<string, number> | undefined): string {
  if (!dist) return "—";
  const parts = ["healthy", "watch", "concerning", "at_risk"]
    .filter((b) => (dist[b] ?? 0) > 0)
    .map((b) => `${dist[b]} ${b.replace("_", "-")}`);
  return parts.join(" · ") || "—";
}

interface PortfolioStatCardsProps {
  /** When set, replaces stats.totalArr in the headline card. Used to keep
   *  the card aligned with the table's per-row ACV mode toggle. */
  arrOverride?: number | null;
  /** Short label rendered as the hint under "Total ARR" so the user knows
   *  which projection mode the headline number reflects. */
  arrModeHint?: string;
}

export function PortfolioStatCards({
  arrOverride,
  arrModeHint,
}: PortfolioStatCardsProps = {}) {
  const { data, isLoading } = usePortfolioOverview();

  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="bg-surface-primary rounded-lg p-4 shadow-sm border border-[#E9E9EB] animate-pulse"
          >
            <div className="h-3 w-24 bg-surface-disabled rounded mb-2" />
            <div className="h-7 w-20 bg-surface-disabled rounded" />
          </div>
        ))}
      </div>
    );
  }

  const stats: PortfolioStats = data;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
      <StatCard
        label="CARR"
        value={formatUsd(stats.totalCarr ?? null)}
        accent="brand"
        hint="Sum of contracted ARR (excludes test accounts)"
      />
      <StatCard
        label="Total ARR"
        value={formatUsd(arrOverride !== undefined ? arrOverride : stats.totalArr)}
        hint={arrModeHint}
      />
      <StatCard
        label="MRR (this month)"
        value={formatUsd(stats.totalMrr)}
        accent="brand"
      />
      <StatCard
        label="30-Day Profit"
        value={formatUsd(stats.totalMargin30d)}
        accent={
          stats.totalMargin30d != null && stats.totalMargin30d < 0
            ? "warn"
            : "brand"
        }
      />
      <StatCard
        label="Active Customers"
        value={formatInt(stats.totalCustomers)}
        hint={bandLabel(stats.healthBandDistribution)}
      />
      <StatCard
        label="At-Risk"
        value={formatInt(stats.atRiskCount)}
        accent={stats.atRiskCount > 0 ? "warn" : "default"}
        hint={`${stats.churnRiskDistribution?.yellow ?? 0} yellow · ${stats.churnRiskDistribution?.red ?? 0} red`}
      />
    </div>
  );
}
